import { useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/** Mutually exclusive choices with a sliding satin selection, with one keyboard tab stop. */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional colour swatch (any CSS colour, e.g. a `var(--swatch-*)`) shown before the label. */
  dot?: string;
  icon?: ReactNode;
  tone?: "neutral" | "image" | "video" | "audio" | "accent";
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  "aria-label": string;
  className?: string;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  disabled?: boolean;
  name?: string;
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  "aria-label": ariaLabel,
  className,
  size = "md",
  fullWidth = false,
  disabled = false,
  name,
}: SegmentedControlProps<T>) {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.findIndex(
    (option) => option.value === value,
  );
  const tabStopIndex =
    selectedIndex >= 0 && !options[selectedIndex]?.disabled
      ? selectedIndex
      : options.findIndex((option) => !option.disabled);

  function move(event: KeyboardEvent<HTMLButtonElement>, from: number): void {
    if (disabled || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const enabled = options
      .map((option, index) => ({ option, index }))
      .filter(({ option }) => !option.disabled);
    if (!enabled.length) return;
    const current = enabled.findIndex(({ index }) => index === from);
    const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
    const forward = event.key === "ArrowDown" || event.key === (rtl ? "ArrowLeft" : "ArrowRight");
    const target =
      event.key === "Home"
        ? enabled[0]
        : event.key === "End"
          ? enabled.at(-1)
          : enabled[
              (current +
                (forward ? 1 : -1) +
                enabled.length) %
                enabled.length
            ];
    if (!target) return;
    onChange(target.option.value);
    itemRefs.current[target.index]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      aria-disabled={disabled || undefined}
      data-tone={options[selectedIndex]?.tone ?? "neutral"}
      data-size={size}
      className={cn("studio-segmented", fullWidth && "studio-segmented-full", className)}
      style={{ "--segment-count": Math.max(1, options.length), "--segment-index": Math.max(0, selectedIndex) } as CSSProperties}
    >
      {name ? <input type="hidden" name={name} value={value} disabled={disabled} /> : null}
      {selectedIndex >= 0 ? <span aria-hidden="true" className="studio-segmented-thumb" /> : null}
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled || option.disabled}
            tabIndex={!disabled && index === tabStopIndex ? 0 : -1}
            data-tone={option.tone ?? "neutral"}
            ref={(node) => {
              itemRefs.current[index] = node;
            }}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => move(event, index)}
            className="studio-segmented-option"
          >
            {option.icon ? <span aria-hidden="true" className="studio-control-icon">{option.icon}</span> : null}
            {option.dot ? (
              <span
                aria-hidden="true"
                className="size-2 shrink-0 rounded-full bg-accent"
                style={{ backgroundColor: option.dot }}
              />
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
